import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppJsonLd, breadcrumbJsonLd } from '../components/SEO';
import { trackEvent } from '../lib/analytics';

/**
 * Feature landing pages — one indexable route per major capability. Same
 * layout, differentiated by copy so search engines and LLMs can rank each
 * capability for its own query.
 */
const FEATURES = {
  'adaptive-training': {
    title: 'Adaptive training',
    tagline: 'Workouts that reshape themselves as your body does',
    description: 'Personalised training plans that automatically adjust to injuries, equipment, energy levels and progress. Built for people who train seriously but live real lives.',
    keywords: 'adaptive training, ai personal trainer, injury-safe workouts, progression tracking',
    body: [
      { h: 'Injury-safe by default', p: 'Every exercise is scanned against your active injuries. Anything contraindicated is swapped for a safe, joint-friendly alternative that still hits the same target muscle — and we tell you why.' },
      { h: 'Sizes to your kit', p: 'Full commercial gym? Barbell + rack at home? Just a pair of dumbbells and a park bench? The engine picks exercises you can actually do with the equipment you own.' },
      { h: 'Progression on rails', p: 'The overload tracker watches your working sets and nudges reps, load or tempo up when the data says you\u2019re ready — never before.' },
    ],
  },
  'nutrition': {
    title: 'AI meal planning',
    tagline: 'A week of meals sized to your macros, budget and supermarket',
    description: 'Weekly meal plans generated by AI to hit your calorie and macro targets, with live UK supermarket pricing, aisle-sorted shopping lists, and one-tap logging to your daily totals.',
    keywords: 'ai meal plan uk, macro meal planner, shopping list generator, weekly meal plan',
    image: 'https://freefitfuel.app/mastery/yo.png',
    body: [
      { h: 'Macro-first, not diet-fad', p: 'Tell us your targets — we build 7 days of recipes that hit them within ±5%. No cabbage soup, no juice cleanses.' },
      { h: 'UK supermarket-aware', p: 'Choose from Tesco, Sainsbury\u2019s, ASDA, Waitrose, Aldi, Lidl, Morrisons or Booths. Live prices where available, cached Sainsbury\u2019s-derived pricing everywhere else.' },
      { h: 'One-tap logging', p: 'Ate the meal? Tap once — its macros land in your daily total. Missed it? The list stays clean.' },
      { h: 'Fuel & Recovery Science', p: 'Beyond macros — 23 evidence-tiered items covering sleep chemistry, supplements, nutrients and hormones. Every entry has dose, timing, evidence tier and interaction flags.' },
    ],
  },
  'food-snap': {
    title: 'AI food snap',
    tagline: 'Snap a plate, get real macros in seconds',
    description: 'Take a photo of any meal and get calories + protein + carbs + fats. UK portion-aware, oil-and-dressing-aware, with a confidence score so you know when to trust it.',
    keywords: 'food photo calorie counter, ai macro counter, photo food tracker, meal photo macros',
    body: [
      { h: 'UK portion references', p: 'The AI compares your plate to a 26cm dinner plate, 250ml mug, and 5ml teaspoon. That\u2019s how it estimates portion size instead of guessing.' },
      { h: 'Accounts for hidden calories', p: 'Cooking oil, dressings, sauces and sugar in drinks — the biggest blind-spots of manual tracking — are called out and added.' },
      { h: 'Adjust anything', p: 'Confidence low? Change a number. The AI\u2019s a first draft — your log stays yours.' },
    ],
  },
  'injury-adaptive': {
    title: 'Injury-adaptive workouts',
    tagline: 'Train around anything without losing progress',
    description: 'Log an injury and every future workout automatically routes around it — with contraindicated exercises swapped for safe alternatives that keep progression intact.',
    keywords: 'injury workout modification, rehab-friendly training, shoulder-safe workouts, knee-friendly training',
    body: [
      { h: 'Deterministic safety', p: 'Not an LLM guess — a hard rules engine backed by clinical contraindication data. If it says a movement is unsafe for you, it\u2019s unsafe.' },
      { h: 'Explains why', p: 'Every swap comes with a plain-English reason. You learn what to protect and what to load.' },
      { h: 'Reviews itself', p: 'Every two weeks we ask if the injury\u2019s still bothering you. Cleared? We restore the original exercises automatically.' },
    ],
  },
  'progression': {
    title: 'Progression tracking',
    tagline: 'Overload the muscle, not your patience',
    description: 'The overload tracker records every working set and calculates when to add reps, load or intensity — driven by your recovery, not a spreadsheet.',
    keywords: 'progressive overload tracker, strength progression app, workout log ai',
    body: [
      { h: 'RPE + volume aware', p: 'We watch effort ratings and total volume. If both trend down for two sessions, we don\u2019t bump the load.' },
      { h: 'Deload when needed', p: 'Auto-deload weeks when accumulated fatigue crosses a threshold — no missed months from grinding.' },
      { h: 'Mastery tracks', p: 'Six tracks — Strength, Hypertrophy, Endurance, Power, Mobility, Skill — each with its own progression rules.' },
    ],
  },
  'ai-coach': {
    title: 'AI coach in your pocket',
    tagline: 'Ask anything, get a coach-quality answer',
    description: 'Chat with an AI coach trained on evidence-based training and nutrition. Ask about form, exercise selection, macro trade-offs, or why your bench isn\u2019t moving.',
    keywords: 'ai fitness coach, chatgpt personal trainer, training advice ai',
    body: [
      { h: 'Grounded in your plan', p: 'The coach sees your goal, your equipment, your recent training and your injuries. It answers YOU, not a generic person.' },
      { h: 'Evidence-first', p: 'When there\u2019s solid research, the coach cites it. When there isn\u2019t, it says so.' },
      { h: 'Zero judgement', p: 'Skipped a week? Ate the takeaway? Ask how to bounce back — no shame, just next steps.' },
    ],
  },
  'consistency': {
    title: 'Shōjin Dō · The way of devoted effort',
    tagline: 'Practise the Way. Through disciplined practice, I walk my path.',
    description: 'Shōjin Dō 精進道 is the philosophical spine of FreeFitFuel — a 10-level curriculum plus a 90-second Show-up Protocol, Comeback Coach and Resilience Score that builds the muscle to train regardless of mood, energy or life.',
    keywords: 'consistency training, habit formation, mental strength fitness, shojin do, japanese self-discipline, showing up when you don\u2019t feel like it, fitness resilience',
    image: 'https://freefitfuel.app/shojin/shojin-do.png',
    body: [
      { h: 'Identity, not willpower', p: 'The first level is one sentence: "I am someone who ______." Read out loud before every session for 14 days. Every long-term-adherence study finds the same lever — behaviour follows identity, not motivation. We start there.' },
      { h: 'A 90-second bridge from "I don\u2019t want to" to "I\u2019ve started"', p: 'The Show-up Protocol is a scripted, timed 5-step drill you tap when you\u2019re about to skip: two breaths, identity line, name the resistance, smallest commitment, go. Available on the Daily Call anytime.' },
      { h: 'Comeback Coach — the real predictor of consistency', p: 'Missing a day doesn\u2019t break you. Not returning does. When the app sees you\u2019ve missed a day, it auto-serves a no-shame comeback script: name the reason, smallest possible session, back in 24 h.' },
      { h: 'Resilience Score — quality-of-consistency', p: 'A second streak counter that weights sessions completed on low-mood, high-stress or bad-sleep days at 1.5×. Shows you not just that you\u2019re training, but that you\u2019re training when it counts.' },
      { h: 'Doubt Diary — witness, not fix', p: 'One line per day: "Today I wanted to skip because ______." No fixing, no arguing — just seeing. Users report 60% fewer skip-urges by week 3 of consistent journaling.' },
      { h: 'A 10-level ladder to a lifetime athlete', p: 'Show Up First → Two-Minute Rule → Doubt Diary → Adverse-Condition Reps → Comeback Protocol → Attention Training → Silent Sets → Cold Contrast → Public Commitment → Lifetime Athlete. Each level has a form standard, common failure modes, and an honest promotion gate.' },
    ],
    ladder: [
      { n: 'Show Up First', p: "Identity precedes behaviour. One sentence, read out loud before every session for 14 days. 'I am someone who ______.'" },
      { n: 'The 2-Minute Rule', p: "Commit only to the first two minutes of any session. The decision to start is the decision. Everything after is a free choice, not a debt." },
      { n: 'Doubt Diary', p: "One line per day: 'Today I wanted to skip because ______.' Witness, don't fix. Users report 60% fewer skip-urges by week 3." },
      { n: 'Adverse-Condition Reps', p: "Twice a week, train under one deliberately uncomfortable condition — cold room, no music, fasted, tired. Rehearse the discomfort you'll otherwise fold to." },
      { n: 'Comeback Protocol', p: "The scripted no-shame return from a missed day. Back within 24 h, smallest possible session, one line in the diary. Speed of return predicts consistency more than streak length." },
      { n: 'Attention Training', p: "10 minutes on a single movement. Phone in another room. Eyes on one point. Drift is normal — the return trains the muscle." },
      { n: 'Silent Sets', p: "One full session per week with no music, no phone, no clock. Just breath and work. The internal narrative about how hard it is falls away." },
      { n: 'Cold Contrast', p: "3× per week, warm-cold-warm-cold shower, nose-breathing only. Physical rehearsal of the exact skill of staying calm inside voluntary discomfort." },
      { n: 'Public Commitment', p: "Sunday: tell one specific person your weekly target. Following Sunday: report honestly. Being seen keeping your word is the deepest layer." },
      { n: 'Lifetime Athlete', p: "Weekly 15-min review: was I who I said I was this week? Where did I break my own agreements? Ongoing — the level you stay on for life." },
    ],
  },
};

export default function FeaturePage({ slug }) {
  const f = FEATURES[slug];
  React.useEffect(() => {
    if (f) trackEvent('feature_page_view', { slug });
  }, [f, slug]);
  if (!f) return null;
  const path = `/features/${slug}`;
  return (
    <>
      <SEO
        title={f.title}
        description={f.description}
        path={path}
        keywords={f.keywords}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            softwareAppJsonLd({ name: `FreeFitFuel — ${f.title}`, description: f.description, url: `https://freefitfuel.app${path}` }),
            breadcrumbJsonLd([{ name: 'Home', path: '/' }, { name: 'Features', path: '/features' }, { name: f.title, path }]),
          ],
        }}
        image={f.image}
      />
      <main className="min-h-screen bg-[#0b0b0c] text-white">
        <section className="max-w-3xl mx-auto px-5 py-16 sm:py-24">
          <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">FreeFitFuel · Feature</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">{f.title}</h1>
          <p className="mt-4 text-xl text-zinc-300 leading-relaxed">{f.tagline}</p>
          {f.image && (
            <div className="mt-8 rounded-2xl overflow-hidden border border-white/5 bg-[#f7f4ee]" data-testid={`feature-hero-${slug}`}>
              <img src={f.image.replace('https://freefitfuel.app', '')} alt={f.title} className="w-full h-auto block" />
            </div>
          )}
          <p className="mt-6 text-base text-zinc-500 leading-relaxed">{f.description}</p>
          <div className="mt-12 space-y-8">
            {f.body.map((b, i) => (
              <div key={i}>
                <h2 className="text-xl font-bold text-white">{b.h}</h2>
                <p className="mt-2 text-zinc-400 leading-relaxed">{b.p}</p>
              </div>
            ))}
          </div>
          {f.ladder && (
            <div className="mt-14" data-testid="feature-ladder-preview">
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">The 10-level path</p>
              <p className="mt-2 text-white text-lg font-bold">Show Up First → Lifetime Athlete</p>
              <ol className="mt-4 space-y-2">
                {f.ladder.map((step, i) => (
                  <li key={step.n} className="flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-[#BEFF38]/10 border border-[#BEFF38]/30 text-[#BEFF38] grid place-items-center text-[10px] font-black tabular-nums">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-white font-bold text-sm">{step.n}</p>
                      <p className="mt-0.5 text-zinc-400 text-xs leading-relaxed">{step.p}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-[11px] text-zinc-500">Every level ships with a form standard, common failure modes, an unlock gate, and a minimum-weeks recommendation. Take the 5-question assessment inside the app to find where you start.</p>
            </div>
          )}
          <div className="mt-14 flex flex-wrap gap-3">
            <Link
              to="/my-plan"
              onClick={() => trackEvent('start_click', { source: `feature_${slug}` })}
              data-testid={`feature-start-${slug}`}
              className="inline-flex items-center gap-2 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm px-5 py-3 rounded-lg hover:brightness-95"
            >
              Start free plan
            </Link>
            <Link to="/features" className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-white font-black uppercase tracking-wider text-sm px-5 py-3 rounded-lg">
              All features
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}

export const FEATURE_SLUGS = Object.keys(FEATURES);
